import { createTheme } from '@mui/material/styles';

const muiTheme = createTheme({
  palette: {
    primary: {
      main: '#046242',
      light: '#22c55e',
      dark: '#14532d',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#F6C531',
      light: '#FFC10D',
      dark: '#d4a017',
      contrastText: '#000000',
    },
    text: {
      primary: '#000000',
      secondary: '#585858',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    grey: {
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Playfair Display", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
    },
    h2: {
      fontFamily: '"Playfair Display", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
    },
    h3: {
      fontFamily: '"Playfair Display", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
    },
    h4: {
      fontFamily: '"Playfair Display", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
    },
    h5: {
      fontFamily: '"Playfair Display", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
    },
    h6: {
      fontFamily: '"Playfair Display", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
    },
    body1: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    body2: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#D3D4D6',
            },
            '&:hover fieldset': {
              borderColor: '#046242',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#046242',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D3D4D6',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#046242',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#046242',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          backgroundColor: '#046242',
          '&:hover': {
            backgroundColor: '#14532d',
          },
        },
      },
    },
    // DatePicker Components - Custom styling for date selection
    // Handles hover, selected, today's date, and disabled states
    MuiPickersDay: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 500,
          color: '#000000',
          '&:hover': {
            backgroundColor: 'rgba(4, 98, 66, 0.10)',
            color: '#000000'
          },
          '&.Mui-selected': {
            backgroundColor: '#046242',
            color: 'white',
            '&:hover': {
              backgroundColor: '#046242'
            }
          },
          '&.MuiPickersDay-today': {
            color: '#046242',
            fontWeight: 'bold',
            '&:not(.Mui-selected)': {
              backgroundColor: 'transparent'
            }
          },
          '&.Mui-disabled': {
            color: '#CECECE',
            opacity: 0.5
          }
        }
      }
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          padding: '16px',
          '& .MuiPickersCalendarHeader-label': {
            fontSize: '20px',
            fontWeight: 500,
            color: '#000000'
          },
          '& .MuiIconButton-root': {
            color: '#000000'
          }
        }
      }
    },
    MuiDayCalendar: {
      styleOverrides: {
        root: {
          padding: '16px'
        }
      }
    },
    MuiPickersPopper: {
      styleOverrides: {
        root: {
          zIndex: 1300
        }
      }
    },
    MuiPickersCalendar: {
      styleOverrides: {
        root: {
          '& .MuiPickersDay-root': {
            fontSize: '14px',
            fontWeight: 500,
            color: '#000000',
            '&:hover': {
              backgroundColor: 'rgba(4, 98, 66, 0.10)',
              color: '#000000'
            },
            '&.Mui-selected': {
              backgroundColor: '#046242',
              color: 'white',
              '&:hover': {
                backgroundColor: '#046242'
              }
            },
            '&.MuiPickersDay-today': {
              color: '#046242',
              fontWeight: 'bold',
              '&:not(.Mui-selected)': {
                backgroundColor: 'transparent'
              }
            },
            '&.Mui-disabled': {
              color: '#CECECE',
              opacity: 0.5
            }
          }
        }
      }
    },
    MuiPickersMonth: {
      styleOverrides: {
        root: {
          '& .MuiPickersMonth-monthButton': {
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(4, 98, 66, 0.10)'
            },
            '&.Mui-selected': {
              backgroundColor: '#046242',
              color: 'white'
            }
          }
        }
      }
    },
    MuiPickersYear: {
      styleOverrides: {
        root: {
          '& .MuiPickersYear-yearButton': {
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(4, 98, 66, 0.10)'
            },
            '&.Mui-selected': {
              backgroundColor: '#046242',
              color: 'white'
            }
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.card-style': {
            borderRadius: 8,
            border: '1px solid #D3D4D6',
            boxShadow: '0 5px 10px 0 rgba(0, 0, 0, 0.10)',
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          '&.card-style': {
            borderRadius: 8,
            border: '1px solid #D3D4D6',
            boxShadow: '0 5px 10px 0 rgba(0, 0, 0, 0.10)',
          }
        }
      }
    },
  },
});

export default muiTheme;
